import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {ExternalLink, Loader, Star} from 'lucide-react';

const GOOGLE_PROFILE = 'https://www.google.com/maps/search/3DLoc+Cauville-sur-Mer';

const Stars = ({value = 5, size = 16}) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} étoiles sur 5`}>
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} size={size} className={i <= Math.round(value) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'} />
    ))}
  </span>
);

export default function AvisClientsPage() {
  const [state, setState] = useState({loading: true});

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`, {signal: controller.signal})
      .then(async response => {
        if (!response.ok) throw new Error('Les avis Google ne peuvent pas être affichés pour le moment.');
        setState({data: await response.json()});
      })
      .catch(error => {if (error.name !== 'AbortError') setState({error: error.message});});
    return () => controller.abort();
  }, []);

  const data = state.data;
  const reviews = data?.reviews || [];
  const profileUrl = data?.googleMapsUri || GOOGLE_PROFILE;
  const writeUrl = data?.writeReviewUri;
  const allFiveStars = reviews.length > 0 && reviews.every(review => review.rating === 5);

  return (
    <div data-testid="avis-clients-page" className="bg-slate-50">
      <section className="bg-slate-900 text-white py-14 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 data-testid="avis-clients-title" className="text-4xl sm:text-5xl font-black mb-4">Avis clients 3DLoc</h1>
          <p className="text-slate-300 text-base max-w-3xl leading-relaxed">
            Particuliers, artisans, entreprises et collectivités louent chez 3DLoc depuis Cauville-sur-Mer, à 15 minutes
            du Havre et d'Étretat.{' '}
            {reviews.length > 0
              ? "Les avis ci-dessous viennent de notre fiche Google : les plus récents sont remontés automatiquement par Google, les autres sont repris mot pour mot. Tous restent consultables et vérifiables sur la fiche, nous n'en rédigeons aucun et nous ne pouvons pas les modifier."
              : "Nos avis sont publiés sur notre fiche Google, où ils restent vérifiables par tout le monde : nous n'en rédigeons aucun et nous ne pouvons pas les modifier."}
          </p>
          {data?.rating > 0 && (
            <div data-testid="avis-clients-rating" className="mt-8 inline-flex flex-wrap items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-4">
              <Stars value={data.rating} size={20} />
              <span className="text-2xl font-bold">{Number(data.rating).toFixed(1)}/5</span>
              {data.userRatingCount > 0 && (
                <span className="text-slate-300 text-sm">sur {data.userRatingCount} avis Google</span>
              )}
            </div>
          )}
          {!data?.rating && allFiveStars && (
            <div data-testid="avis-clients-summary" className="mt-8 inline-flex flex-wrap items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-4">
              <Stars value={5} size={20} />
              <span className="text-slate-200 text-sm">{reviews.length} avis Google affichés, tous notés 5/5</span>
            </div>
          )}
        </div>
      </section>

      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {state.loading && !data && (
            <div data-testid="avis-clients-loading" role="status" className="flex justify-center py-10">
              <Loader className="animate-spin text-green-600" aria-label="Chargement des avis" />
            </div>
          )}

          {state.error && (
            <p role="alert" data-testid="avis-clients-error" className="bg-white border border-slate-100 rounded-xl p-6 text-slate-600">
              {state.error} Vous pouvez consulter l'ensemble de nos avis directement sur Google.
            </p>
          )}

          {data && !data.configured && (
            <div data-testid="avis-clients-fallback" className="bg-white border border-slate-100 rounded-xl p-6 sm:p-8 text-slate-600 leading-relaxed space-y-4">
              <p className="text-slate-800 font-semibold">Nos avis sont publiés sur notre fiche Google.</p>
              <p>
                Pour garantir leur authenticité, nous n'affichons aucun avis rédigé par nos soins : retrouvez-les tous,
                avec leur auteur et leur date, directement sur Google.
              </p>
            </div>
          )}

          {reviews.map((review, index) => (
            <article key={review.uri || index} data-testid={`avis-client-${index}`} className="bg-white border border-slate-100 rounded-xl p-6 sm:p-8">
              <div className="flex items-start gap-4">
                {review.authorPhoto && (
                  <img src={review.authorPhoto} alt="" loading="lazy" className="w-11 h-11 rounded-full object-cover shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">
                    {review.authorUri ? (
                      <a href={review.authorUri} target="_blank" rel="noopener noreferrer nofollow" className="hover:text-green-600 transition-colors">{review.author}</a>
                    ) : review.author}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-1">
                    <Stars value={review.rating} />
                    {review.relativeTime && <span className="text-sm text-slate-500">{review.relativeTime}</span>}
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed whitespace-pre-line">{review.text}</p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                {review.uri ? (
                  <a href={review.uri} target="_blank" rel="noopener noreferrer nofollow" data-testid={`avis-client-source-${index}`} className="inline-flex items-center gap-1 text-green-700 hover:text-green-800 transition-colors">
                    Voir cet avis sur Google Maps <ExternalLink size={14} />
                  </a>
                ) : (
                  <a href={profileUrl} target="_blank" rel="noopener noreferrer nofollow" data-testid={`avis-client-source-${index}`} className="inline-flex items-center gap-1 text-green-700 hover:text-green-800 transition-colors">
                    Vérifier sur notre fiche Google <ExternalLink size={14} />
                  </a>
                )}
                {review.flagUri && (
                  <a href={review.flagUri} target="_blank" rel="noopener noreferrer nofollow" className="text-slate-400 hover:text-slate-600 transition-colors">Signaler</a>
                )}
              </div>
            </article>
          ))}

          <div className="bg-white border border-slate-100 rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div>
              <p className="font-semibold text-slate-800">Vous avez loué chez 3DLoc ?</p>
              <p className="text-sm text-slate-600 mt-1">Votre avis aide les prochains clients à choisir leur matériel.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={profileUrl} target="_blank" rel="noopener noreferrer" data-testid="avis-clients-google-link" className="inline-flex items-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-700 transition-colors text-white font-semibold rounded-lg">
                Voir les avis sur Google <ExternalLink size={16} />
              </a>
              {writeUrl && (
                <a href={writeUrl} target="_blank" rel="noopener noreferrer" data-testid="avis-clients-write-link" className="px-5 py-3 border border-green-600 text-green-700 hover:bg-green-50 transition-colors font-semibold rounded-lg">
                  Laisser un avis
                </a>
              )}
              <Link to="/contact" data-testid="avis-clients-contact-link" className="px-5 py-3 border border-green-600 text-green-700 hover:bg-green-50 transition-colors font-semibold rounded-lg">
                Demander un devis
              </Link>
            </div>
          </div>

          <p translate="no" aria-label="Attribution Google Maps" className="text-xs text-slate-400">Google Maps</p>
        </div>
      </section>
    </div>
  );
}
